import { Box, Drawer, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { MdOutlineModeEdit } from 'react-icons/md';
import Button from 'components/Common/Button';
import type { InteractionReport } from '@/apis/business/agent/callLogs/types';
import { useUpdateInteractionLog } from '@/apis/business/agent/callLogs/hook';
import { formatDate } from 'utils/helperFunctions';

type ReportCallDrawerProps = {
  open: boolean;
  onClose: () => void;
  interactionId?: number;
  sessionId?: string;
  report?: InteractionReport | null;
  readOnly?: boolean;
};

export default function ReportCallDrawer({
  open,
  onClose,
  interactionId,
  sessionId,
  report,
  readOnly = false
}: ReportCallDrawerProps) {
  const isReported = Boolean(report?.description);
  const [isEditing, setIsEditing] = useState(!isReported && !readOnly);
  const [description, setDescription] = useState(report?.description ?? '');

  const { mutateAsync: updateInteraction, isPending } = useUpdateInteractionLog();

  useEffect(() => {
    if (!open) return;
    setIsEditing(!report?.description && !readOnly);
    setDescription(report?.description ?? '');
  }, [open, report, readOnly]);

  const handleClose = () => {
    setIsEditing(!report?.description && !readOnly);
    onClose();
  };

  const handleSubmit = async () => {
    if (readOnly || !interactionId || !description.trim()) return;

    await updateInteraction({
      id: interactionId,
      payload: { interaction: { report_call: { description: description.trim() } } }
    });
    setIsEditing(false);
  };

  const showForm = !readOnly && (!isReported || isEditing);
  const showDetails = isReported && (readOnly || !isEditing);

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={handleClose}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 420 },
          borderRadius: 0
        }
      }}
    >
      <Box display='flex' flexDirection='column' height='100%'>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          px={3}
          py={2}
          borderBottom='1px solid #DEDEDE'
        >
          <Typography variant='h6'>{showDetails ? 'Reported Issues' : 'Report Call'}</Typography>
          <IconButton onClick={handleClose}>
            <IoClose size={20} />
          </IconButton>
        </Stack>

        <Box p={3} flex={1}>
          {showDetails && (
            <Box
              sx={{
                position: 'relative',
                border: '1px solid #ECECEC',
                borderRadius: 2,
                p: 2.5,
                bgcolor: '#fff'
              }}
            >
              {!readOnly && (
                <IconButton
                  onClick={() => setIsEditing(true)}
                  size='small'
                  sx={{ position: 'absolute', top: 12, right: 12, color: '#2563EB' }}
                >
                  <MdOutlineModeEdit size={18} />
                </IconButton>
              )}
              <DetailRow label='Session ID' value={report?.session_id || sessionId || '—'} />
              <Stack direction='row' justifyContent='space-between'>
                <DetailRow label='Flagged By' value={report?.flagged_by_name || '—'} />
                <DetailRow label='Flagged On' value={formatDate(report?.flagged_at)} />
              </Stack>
              <Box>
                <Typography fontSize={13} color='text.secondary'>
                  Flagged Issue
                </Typography>
                <Typography fontSize={14}>{report?.description}</Typography>
              </Box>
            </Box>
          )}

          {showForm && (
            <Box mt={isReported ? 2 : 0}>
              <Typography fontSize={14} fontWeight={500} mb={1}>
                Describe Issue <span style={{ color: '#DA0808' }}>*</span>
              </Typography>
              <TextField
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder='Describe the issue with this call'
                multiline
                minRows={5}
                fullWidth
              />
            </Box>
          )}
        </Box>

        {showForm && (
          <Stack direction='row' justifyContent='flex-end' gap={1.5} px={3} py={2} borderTop='1px solid #DEDEDE'>
            <Button variant='outlined' onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={handleSubmit}
              isPending={isPending}
              disabled={!description.trim() || !interactionId}
            >
              Submit
            </Button>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Box mb={2}>
      <Typography fontSize={13} color='text.secondary'>
        {label}
      </Typography>
      <Typography fontSize={14}>{value}</Typography>
    </Box>
  );
}
